import { Box, Divider, IconButton, Modal } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { RxCross2 } from "react-icons/rx";
import { toast } from "react-toastify";
import {
  buildAssignAnotherInsurancePayload,
  resetBillingSelectionFields,
} from "./PendingInsuranceChangeInsuranceModal.helpers";
import RequisitionType from "Services/Requisition/RequisitionTypeService";
import InsuranceService from "Services/InsuranceService/InsuranceService";
import { Loader } from "Shared/Common/Loader";
import DynamicFormInputs from "Shared/DynamicFormInputs";
import useYupForm from "Shared/hooks/Requisition/useYupForm";
import { countKeysWithValues } from "Utils/Common/Requisition";
import { ValidateInput } from "Utils/Requisition/Validation";

interface PendingInsuranceChangeInsuranceModalProps {
  open: boolean;
  onClose: () => void;
  rowData: any;
  selectedRequisitionOrders: any[];
  loadGridData?: (loader: boolean, reset: boolean) => void;
}

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 800,
  maxHeight: "80vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  px: 3,
  py: 2,
  borderRadius: 2,
  display: "flex",
  flexDirection: "column",
};

const PendingInsuranceChangeInsuranceModal: React.FC<
  PendingInsuranceChangeInsuranceModalProps
> = ({ open, onClose, rowData, selectedRequisitionOrders, loadGridData }) => {
  const [ins, setIns] = useState(false);
  const [data, setData] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [checkbox, setCheckbox] = useState(false);
  const [formState, setFormState] = useState<any>();
  const [showButton, setShowButton] = useState(true);
  const [disableCheckbox, setDisableCheckbox] = useState(false);
  const [isAddButtonDisabled, setIsAddButtonDisabled] = useState(false);
  console.log("ins>", ins);
  const {
    inputsForValidation,
    setInputsForValidation,
    submitForValidation,
  }: any = useYupForm(data, ValidateInput);

  const fallbackBillingTypeOptions = useMemo(
    () =>
      data?.[0]?.fields?.find(
        (field: any) =>
          `${field?.systemFieldName || ""}`.toLowerCase() === "billingtype"
      )?.options || [],
    [data]
  );

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await RequisitionType.LoadBillingSection(0);
      const cleanedConfig = resetBillingSelectionFields(response.data);

      setData([cleanedConfig]);
      setInputsForValidation([cleanedConfig]);
    } catch (error) {
      console.error("Error loading data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadData();
      const facilityId = rowData?.FacilityId ?? rowData?.facilityId ?? "";
      localStorage.setItem("facilityID", facilityId?.toString() || "");
      localStorage.removeItem("insurnceID");
      localStorage.removeItem("insuranceOptionId");
      sessionStorage.removeItem("billingInsurances");
    }
  }, [open]);

  const handleSave = async () => {
    const validatedData = await submitForValidation(true, false);
    setInputsForValidation(validatedData?.data);
    const validationCount = await countKeysWithValues(
      validatedData?.validation
    );
    if (validationCount > 0) return;

    if (!selectedRequisitionOrders?.length) {
      toast.error("Please select at least one requisition order");
      return;
    }

    const assignAnotherInsurance = buildAssignAnotherInsurancePayload(
      validatedData?.data || [],
      data,
      { formState, fallbackBillingTypeOptions }
    );
    if (!assignAnotherInsurance.length) {
      toast.error("Please fill insurance details");
      return;
    }

    setIsAddButtonDisabled(true);
    try {
      const response =
        await InsuranceService.assignOrderToAnotherInsuranceProvider({
          anotherInsuranceProviderOrderInfo: selectedRequisitionOrders.map(
            (order: any) => ({
              requisitonInsurancePatientId:
                order.requisitonInsurancePatientId || 0,
              requisitionId: order.requisitionId || 0,
              requisitionOrderId: order.requisitionOrderId || 0,
              reqType: order.requisitionType || "",
            })
          ),
          assignAnotherInsurance,
        });
      const apiMessage = response?.data?.message;
      loadGridData?.(true, false);
      onClose();
      toast.success(
        typeof apiMessage === "string" && apiMessage.trim()
          ? apiMessage
          : "Insurance provider assigned successfully"
      );
    } catch (error: any) {
      const data = error?.response?.data;
      const errMsg =
        (typeof data === "string" && data) ||
        data?.message ||
        data?.title ||
        error?.message ||
        "Failed to assign insurance provider";
      toast.error(errMsg);
    } finally {
      setIsAddButtonDisabled(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="data-update-modal-title"
    >
      <Box
        sx={{
          ...modalStyle,
          "& .btn.btn-light-primary.btn-sm.px-4.p-2.py-3": {
            display: "none !important",
          },
          "& .btn.btn-primary.sm.mr-2.mt-2.order-2": {
            display: "none !important",
          },
          "& button.btn.btn-info:has(i.fa)": { display: "none !important" },
        }}
      >
        <div className="card-header minh-42px d-flex justify-content-between align-items-center">
          <h3 className="m-0 fs-15px d-flex">
            <div>{data[0]?.sectionName}</div>
            <span
              dangerouslySetInnerHTML={{
                __html: data[0]?.cssStyle,
              }}
            ></span>
          </h3>
          <IconButton aria-label="cross" onClick={onClose}>
            <RxCross2 />
          </IconButton>
        </div>
        {loading ? (
          <Loader />
        ) : (
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              pr: 1,
            }}
          >
            <div className="row d-flex">
              {Array.isArray(inputsForValidation) &&
                inputsForValidation[0]?.fields?.map(
                  (field: any, index: number) => (
                    <DynamicFormInputs
                      key={`${field?.controlId || field?.systemFieldName}-${index}`}
                      uiType={field?.uiType}
                      label={field?.displayFieldName}
                      length={field?.length}
                      disabled={field?.disabled}
                      defaultValue={field?.defaultValue ?? ""}
                      displayType={field?.displayType}
                      visible={field?.visible}
                      required={field?.required}
                      RadioOptions={
                        field?.uiType === "RadioButton" ||
                        field?.uiType === "RadioButtonWithText" ||
                        field?.uiType === "RadioQuestion" ||
                        field?.uiType === "CheckBoxList" ||
                        field?.uiType === "italicizeCheckboxList" ||
                        field?.uiType === "DropDown" ||
                        field?.uiType === "ServerSideDynamicDropDown"
                          ? field?.options
                          : ""
                      }
                      panels={field?.panels ?? []}
                      fieldConfiguration={field?.fieldConfiguration}
                      specimenSources={field?.specimenSources ?? []}
                      formState={formState}
                      setFormState={setFormState}
                      index={0}
                      fieldIndex={index}
                      Inputs={inputsForValidation}
                      setInputs={setData}
                      sysytemFieldName={field?.systemFieldName ?? "undefined"}
                      isDependent={false}
                      controlId={field?.controlId}
                      searchID={field?.searchID}
                      isDependency={false}
                      removeUi={field?.removeUi ? field?.removeUi : false}
                      recursiveDependencyControls={
                        field?.showDep ? field?.dependencyControls : false
                      }
                      showRecursiveDep={field?.showDep ? field?.showDep : false}
                      section={inputsForValidation[0]}
                      repeatFields={field?.repeatFields}
                      repeatDependencyControls={field?.repeatDependencyControls}
                      repeatFieldsState={field?.repeatFieldsState}
                      repeatDependencyControlsState={
                        field?.repeatDependencyControlsState
                      }
                      field={field}
                      mask={field?.mask}
                      enableRule={field?.enableRule}
                      setIns={setIns}
                      disableCheckbox={disableCheckbox}
                      setDisableCheckbox={setDisableCheckbox}
                      checkbox={checkbox}
                      setCheckbox={setCheckbox}
                      showButton={showButton}
                      setShowButton={setShowButton}
                      fields={field}
                    />
                  )
                )}
            </div>
          </Box>
        )}
        <Divider
          sx={{
            borderColor: "var(--bs-card-cap-bg)",
            marginY: 2,
          }}
        />
        <div className="d-flex align-items-cente flex-end gap-2">
          <button
            className="btn btn-danger btn-sm fw-bold mr-3 px-5 text-capitalize text-white h-30px"
            type="button"
            onClick={onClose}
          >
            <span>
              <span>Cancel</span>
            </span>
          </button>
          <button
            className="btn btn-primary btn-sm fw-bold mr-3 px-5 text-capitalize h-30px"
            type="button"
            onClick={handleSave}
            disabled={isAddButtonDisabled || loading}
          >
            <span>
              <span>Save</span>
            </span>
          </button>
        </div>
      </Box>
    </Modal>
  );
};

export default PendingInsuranceChangeInsuranceModal;
